import { FC, FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import countryService, { TravelIntentResult } from "services/countryService";

const TravelIntentBlock: FC = () => {
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<TravelIntentResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (!text.trim()) {
      setError("Dis-nous en quelques mots ce que tu recherches.");
      setResult(null);
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await countryService.searchTravelIntent(text);
      if (!response) {
        setError("Je n'ai pas encore trouvé de destination pour cette envie.");
        return;
      }

      setResult(response);
    } catch {
      setError("La recherche n'a pas abouti. Réessaie dans un instant.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container
      maxWidth="md"
      sx={{
        py: { xs: 3, md: 4 },
      }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          backgroundColor: "white",
          border: "1px solid rgba(0, 43, 96, 0.12)",
          borderRadius: "8px",
          boxShadow: "0 10px 30px rgba(0, 43, 96, 0.08)",
          p: { xs: 2, md: 3 },
        }}
      >
        <Stack spacing={2}>
          <Box>
            <Typography variant="h5" color="primary">
              Où veux-tu partir ?
            </Typography>
            <Typography variant="body1" color="primary">
              Décris ton envie de voyage, Caladre te propose un pays et une ambiance.
            </Typography>
          </Box>

          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
            <TextField
              fullWidth
              value={text}
              disabled={loading}
              onChange={(event) => setText(event.target.value)}
              placeholder="Mer, montagne, apéros, nature..."
              inputProps={{ maxLength: 300 }}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress color="inherit" size={18} /> : <TravelExploreIcon />}
              sx={{
                minWidth: { xs: "100%", md: 180 },
                whiteSpace: "nowrap",
              }}
            >
              {loading ? "Recherche..." : "Trouver"}
            </Button>
          </Stack>

          {error && <Alert severity="info">{error}</Alert>}

          {result && (
            <Box
              sx={{
                borderTop: "1px solid rgba(0, 43, 96, 0.12)",
                pt: 2,
              }}
            >
              <Typography variant="body1" color="primary">
                On te propose <strong>{result.country.name}</strong>.
              </Typography>
              <Typography variant="body2" color="primary" sx={{ mt: 0.5 }}>
                Mots reconnus : {result.matchedKeywords.map((keyword) => keyword.keyword).join(", ")}
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate(`/fiche-pays/${result.country.id}`)}
                >
                  Voir la fiche pays
                </Button>
                {result.alternatives.length > 0 && (
                  <Typography variant="body2" color="primary" sx={{ alignSelf: "center" }}>
                    Aussi possible : {result.alternatives.map((country) => country.countryName).join(", ")}
                  </Typography>
                )}
              </Stack>
            </Box>
          )}
        </Stack>
      </Box>
    </Container>
  );
};

export default TravelIntentBlock;
